// src/utils/ghostThresholds.js
// Per-state (and eventually per-agency) auto-ghost thresholds. Replaces the
// flat 150-day GHOST_AGE_DAYS: real response-time data shows sharply
// different normal ranges by state (IL responds by day 31 in 90% of cases;
// NM is still within normal range past 200 days), so one global number was
// flagging fast states too late and slow states too early. (3.1)

export const GHOST_THRESHOLDS = {
  global: 150,
  state: {
    GA: 30,
    NJ: 30,
    OK: 40,
    IL: 45,
    WI: 60,
    ID: 60,
    WA: 150,
    NM: 240,
  },
  agency: {
    // empty for now — populated later, one at a time, only when Glenn
    // confirms a specific agency is reliably slow. Do not seed this
    // from historical data without explicit confirmation.
  },
};

// Precedence: agency override -> state override -> global default.
export function resolveGhostThreshold(agencyName, state) {
  if (GHOST_THRESHOLDS.agency[agencyName] != null) return GHOST_THRESHOLDS.agency[agencyName];
  if (state && GHOST_THRESHOLDS.state[state] != null) return GHOST_THRESHOLDS.state[state];
  return GHOST_THRESHOLDS.global;
}

// "Ghost @ N" pill data for a Pending record — read-side only, no new
// Firestore fields. Deliberately resolves agencyName/state the same way
// runAutoGhostSweep does, so what the pill promises always matches what the
// sweep will actually do. (3.1c)
//
// status: 'default' (quiet, still well within the window),
//         'approaching' (last 14 days before the threshold),
//         'overdue' (past threshold but the sweep hasn't run yet).
export function getGhostPillInfo(agencyName, state, displayDate, now = new Date()) {
  if (!displayDate) return null;
  const threshold = resolveGhostThreshold(agencyName, state);
  const daysSince = Math.floor((now.getTime() - new Date(displayDate).getTime()) / 86400000);
  const daysLeft = threshold - daysSince;

  let status, label;
  if (daysLeft < 0) {
    status = 'overdue';
    label = 'Overdue';
  } else if (daysLeft <= 14) {
    status = 'approaching';
    label = `Ghost @ ${threshold} · ${daysLeft}d left`;
  } else {
    status = 'default';
    label = `Ghost @ ${threshold}`;
  }

  let tooltip;
  if (GHOST_THRESHOLDS.agency[agencyName] != null) {
    tooltip = `Agency override: ${threshold} days`;
  } else if (state && GHOST_THRESHOLDS.state[state] != null) {
    tooltip = `State default (${state}): ${threshold} days`;
  } else {
    tooltip = `Global default: ${threshold} days`;
  }

  return { status, label, tooltip, threshold, daysSince, daysLeft };
}
